// Abschnitt „LLM-Konfiguration" in den Projekt-Einstellungen (Issue #359).
//
// Je Anbieter ein Schlüsselfeld und eine Modellwahl. Zwei Dinge, die diese Ansicht anders macht
// als ein gewöhnliches Formular:
//
//   1. **Der Schlüssel kommt nie zurück.** Angezeigt wird nur, OB einer hinterlegt ist, in welcher
//      Form (OpenBao-Handle oder verschlüsselt) und seine letzten vier Zeichen. Das Eingabefeld
//      ist deshalb immer leer und heißt „ersetzen", nicht „bearbeiten" — ein Feld, das den alten
//      Wert vorbelegt, gäbe es nur, wenn die API ihn herausrückte.
//   2. **Die effektive Auflösung steht daneben.** Ein Projekt ohne eigenen Schlüssel benutzt den
//      des Nutzers oder den der Appliance. Ohne diese Anzeige rätselt man bei einem unerwarteten
//      Anbieter-Konto, welche Ebene gegriffen hat.
//
// Ist auf dem Server kein Verschlüsselungsschlüssel konfiguriert, sagt die Ansicht das VOR der
// Eingabe: dann sind nur OpenBao-Handles zulässig, und ein eingetippter Schlüssel würde abgewiesen.

import { useCallback, useEffect, useState, type JSX } from 'react';
import { getProjectLlmConfig, setProjectLlmConfig } from '../api/client';
import type { LlmConfigUpdate, ProjectLlmConfig, LlmProviderView } from '../api/types';
import { useI18n } from '../i18n';
import { Button, LabeledInput, LabeledSelect, PanelHeading } from '../components/ui';

type Meldung = { kind: 'ok' | 'fehler'; text: string } | null;

const ANBIETER = [
  { id: 'anthropic' as const, label: 'Anthropic' },
  { id: 'openai' as const, label: 'OpenAI' },
];

export function LlmConfigPanel({ projectId }: { projectId: string }): JSX.Element {
  const { t } = useI18n();
  const [config, setConfig] = useState<ProjectLlmConfig | null>(null);
  const [schluessel, setSchluessel] = useState<Record<string, string>>({});
  const [modelle, setModelle] = useState<Record<string, string>>({});
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [meldung, setMeldung] = useState<Meldung>(null);

  const laden = useCallback(async () => {
    try {
      const doc = await getProjectLlmConfig(projectId);
      setConfig(doc);
      setModelle({
        anthropic: doc.project.anthropic.model ?? '',
        openai: doc.project.openai.model ?? '',
      });
      setSchluessel({});
      setMeldung(null);
    } catch (err) {
      setMeldung({ kind: 'fehler', text: String(err) });
    }
  }, [projectId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function speichern(): Promise<void> {
    setBeschaeftigt(true);
    try {
      const patch: LlmConfigUpdate = {};
      if (schluessel.anthropic?.trim()) patch.anthropicApiKey = schluessel.anthropic.trim();
      if (schluessel.openai?.trim()) patch.openaiApiKey = schluessel.openai.trim();
      if (modelle.anthropic) patch.anthropicModel = modelle.anthropic;
      if (modelle.openai) patch.openaiModel = modelle.openai;
      const doc = await setProjectLlmConfig(projectId, patch);
      setConfig(doc);
      setSchluessel({});
      setMeldung({ kind: 'ok', text: t('settings.llm.gespeichert') });
    } catch (err) {
      setMeldung({ kind: 'fehler', text: String(err) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  async function schluesselEntfernen(anbieter: 'anthropic' | 'openai'): Promise<void> {
    setBeschaeftigt(true);
    try {
      const doc = await setProjectLlmConfig(projectId, {
        [anbieter === 'anthropic' ? 'clearAnthropicKey' : 'clearOpenaiKey']: true,
      } as LlmConfigUpdate);
      setConfig(doc);
      setMeldung({ kind: 'ok', text: t('settings.llm.entfernt') });
    } catch (err) {
      setMeldung({ kind: 'fehler', text: String(err) });
    } finally {
      setBeschaeftigt(false);
    }
  }

  // Der hinterlegte Stand in Worten: „OpenBao-Handle (…#key)" bzw. „verschlüsselt (…Ab12)".
  function schluesselStand(v: LlmProviderView): string {
    if (!v.keySet) return t('settings.llm.keinSchluessel');
    const art = v.keyKind === 'bao' ? t('settings.llm.artBao') : t('settings.llm.artEnc');
    return `${art} (…${v.keyHint ?? ''})`;
  }

  if (!config) {
    return (
      <section className="flex flex-col gap-2" data-testid="llm-config-panel">
        <PanelHeading>{t('settings.llm.heading')}</PanelHeading>
        <p className="text-sm text-bf-muted">{t('settings.llm.laden')}</p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4" data-testid="llm-config-panel">
      <div>
        <PanelHeading>{t('settings.llm.heading')}</PanelHeading>
        <p className="text-sm text-bf-muted">{t('settings.llm.intro')}</p>
        {!config.encryptionAvailable && (
          // Vor der Eingabe sagen, nicht danach: ohne Server-Schlüssel wird ein eingetippter
          // Schlüssel abgewiesen, damit er nicht im Klartext in einem git-Repo landet.
          <p className="mt-1 text-sm text-amber-400" data-testid="llm-no-encryption">
            {t('settings.llm.keineVerschluesselung')}
          </p>
        )}
      </div>

      {ANBIETER.map(({ id, label }) => {
        const stand = config.project[id];
        const effektiv = config.effective.find((e) => e.provider === id);
        return (
          <div key={id} className="rounded border border-bf-border p-3" data-testid={`llm-${id}`}>
            <h4 className="text-sm font-semibold text-bf-white">{label}</h4>
            <p className="mt-1 text-xs text-bf-muted" data-testid={`llm-${id}-state`}>
              {t('settings.llm.projektStand')}: {schluesselStand(stand)}
            </p>
            {effektiv && (
              <p className="text-xs text-bf-muted" data-testid={`llm-${id}-effective`}>
                {t('settings.llm.effektiv', {
                  model: effektiv.model,
                  keyFrom: effektiv.keySet
                    ? t(`settings.llm.ebene.${effektiv.keyFrom ?? 'server'}`)
                    : t('settings.llm.keinSchluessel'),
                  modelFrom: t(`settings.llm.ebene.${effektiv.modelFrom ?? 'default'}`),
                })}
              </p>
            )}
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <LabeledInput
                label={t('settings.llm.schluessel')}
                testId={`llm-${id}-key`}
                type="password"
                value={schluessel[id] ?? ''}
                placeholder={t('settings.llm.schluesselPlatzhalter')}
                onChange={(v) => setSchluessel((s) => ({ ...s, [id]: v }))}
              />
              <LabeledSelect
                label={t('settings.llm.modell')}
                testId={`llm-${id}-model`}
                value={modelle[id] ?? ''}
                onChange={(v) => setModelle((m) => ({ ...m, [id]: v }))}
              >
                <option value="">{t('settings.llm.modellErben')}</option>
                {(config.models[id] ?? []).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </LabeledSelect>
            </div>
            {stand.keySet && (
              <Button
                testId={`llm-${id}-clear`}
                onClick={() => void schluesselEntfernen(id)}
                disabled={beschaeftigt}
              >
                {t('settings.llm.entfernen')}
              </Button>
            )}
          </div>
        );
      })}

      <div className="flex items-center gap-3">
        <Button testId="llm-save" onClick={() => void speichern()} disabled={beschaeftigt}>
          {t('settings.llm.speichern')}
        </Button>
        {meldung && (
          <span
            className={`text-sm ${meldung.kind === 'ok' ? 'text-bf-white' : 'text-red-400'}`}
            data-testid="llm-message"
          >
            {meldung.text}
          </span>
        )}
      </div>
    </section>
  );
}
